import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CalendarClock, CircleDollarSign, Layers } from 'lucide-react';
import { PricingUnit } from '@/lib/domain';
import { formatCurrency } from '@/lib/i18n';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { SwitchRow } from '@/components/ui/switch';
import { pricingListRulePath, pricingListRulesPath } from '../pricingRoutes';
import { refusalOf, type Refusal } from '../lib/refusals';
import type { PricingRuleRow } from '../pricingListsApi';
import {
  DAY_KEYS,
  RULE_BOUNDS,
  blankDraft,
  bodyOf,
  draftOf,
  problemFor,
  ruleProblems,
  workedExamples,
  type RuleDraft,
  type RuleField,
  type RuleProblem,
} from '../lib/rule-draft';
import { RefusalNotice } from './RefusalNotice';

/**
 * One rate on a rate card.
 *
 * It used to be one rate on one PlayStation, which is the whole of what changed:
 * a venue with ten consoles typed the same four numbers ten times, and putting
 * the hourly rate up meant editing ten of them — ten chances for nine to still
 * be right afterwards, with no screen anywhere that could show the disagreement.
 * The form is nearly unchanged, because the form was never the problem; what it
 * writes to is.
 *
 * A rule has eleven fields and they are not eleven equal questions. Presented
 * flat, the form would ask a venue that charges 40 an hour to think about block
 * lengths, day masks, clock windows and a tie-break integer before it could type
 * `40`. So it is three decisions in the order they are actually made: **what it
 * costs** (always), **when it applies** (usually never — most venues have one
 * rate), and **which rule wins** (only once there is more than one). The second
 * and third are switched off until they are asked for, and the first carries a
 * worked example, because the whole point of the screen is a price somebody will
 * be charged.
 */
interface FormProps {
  open: boolean;
  /** The rate card this rule belongs to. Null closes the form. */
  listId: string | null;
  /** The rule being edited, or null to add one. */
  record: PricingRuleRow | null;
  currency: string;
  /** How many rules are already in force on this card — what makes `priority` matter. */
  activeRuleCount: number;
  onClose: () => void;
  onSaved: () => void;
}

export function PricingRuleFormDialog({
  open,
  listId,
  record,
  currency,
  activeRuleCount,
  onClose,
  onSaved,
}: FormProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<RuleDraft>(blankDraft);
  const [windowed, setWindowed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  /** Objections are held back until the first save, so a blank form is not red. */
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = record ? draftOf(record) : blankDraft();
    setDraft(next);
    setWindowed(Boolean(next.startTime && next.endTime) || next.daysOfWeek.length > 0);
    setRefusal(null);
    setSubmitted(false);
  }, [open, record]);

  const set = (patch: Partial<RuleDraft>) => setDraft((current) => ({ ...current, ...patch }));

  const problems = useMemo(() => ruleProblems(draft), [draft]);
  const shown = submitted ? problems : [];
  const errorFor = (field: RuleField) => messageOf(problemFor(shown, field), t);

  const toggleDay = (day: number) =>
    setDraft((current) => ({
      ...current,
      daysOfWeek: current.daysOfWeek.includes(day)
        ? current.daysOfWeek.filter((value) => value !== day)
        : [...current.daysOfWeek, day],
    }));

  /**
   * Turning the window off clears both ends and the day mask rather than merely
   * hiding them: a hidden `startTime` still travels in the body, and a manager
   * who switched "only at certain times" off would have saved a rule that still
   * only applied then.
   */
  const setWindowEnabled = (next: boolean) => {
    setWindowed(next);
    if (!next) set({ startTime: '', endTime: '', daysOfWeek: [] });
  };

  const save = async () => {
    setSubmitted(true);
    if (!listId || problems.length > 0) return;
    setSaving(true);
    setRefusal(null);
    try {
      const body = bodyOf(draft);
      if (record) {
        await api.patch(pricingListRulePath(listId, record.id), body);
      } else {
        await api.post(pricingListRulesPath(listId), body);
      }
      toast.success(t(record ? 'spaces.pricingRules.updated' : 'spaces.pricingRules.created'));
      onSaved();
      onClose();
    } catch (error) {
      // Held on the dialog rather than thrown at a toast: the 409 names another
      // rule on this same card, and the fix is one field on the draft in front
      // of them — which a notice that has already faded cannot point at.
      setRefusal(refusalOf(error));
    } finally {
      setSaving(false);
    }
  };

  const isFixedDuration = draft.unit === PricingUnit.FIXED_DURATION;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(record ? 'spaces.pricingRules.editTitle' : 'spaces.pricingRules.newTitle', {
        name: record?.name ?? '',
      })}
      className="max-w-xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? t('settings.common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* ---- what it costs -------------------------------------------- */}
        <section className="space-y-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <CircleDollarSign className="h-4 w-4 text-accent" aria-hidden />
            {t('spaces.pricingRules.costHeading')}
          </h3>

          <Field
            label={t('spaces.pricingRules.name')}
            hint={t('spaces.pricingRules.nameHint')}
            error={errorFor('name')}
            required
          >
            <Input
              value={draft.name}
              dir="auto"
              placeholder={t('spaces.pricingRules.namePlaceholder')}
              onChange={(event) => set({ name: event.currentTarget.value })}
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('spaces.pricingRules.unit')}>
              <Select
                value={draft.unit}
                onChange={(event) => set({ unit: event.currentTarget.value as PricingUnit })}
              >
                {Object.values(PricingUnit).map((value) => (
                  <option key={value} value={value}>
                    {t(`spaces.pricingRules.unitName.${value}`)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label={t('spaces.pricingRules.price', { currency })}
              hint={t('spaces.pricingRules.priceHint', { places: 4 })}
              error={errorFor('price')}
              required
            >
              <Input
                value={draft.price}
                inputMode="decimal"
                dir="ltr"
                className="text-start"
                placeholder="40.00"
                onChange={(event) => set({ price: event.currentTarget.value })}
              />
            </Field>
          </div>

          <p className="text-xs text-ink-muted">
            {t(`spaces.pricingRules.unitExplainer.${draft.unit}`)}
          </p>

          {/* Only for FIXED_DURATION: the API refuses `durationMinutes` on every
              other unit, so offering the box there would be offering a 400. */}
          {isFixedDuration && (
            <Field
              label={t('spaces.pricingRules.blockLength')}
              hint={t('spaces.pricingRules.rangeHint', { ...RULE_BOUNDS.durationMinutes })}
              error={errorFor('durationMinutes')}
              required
            >
              <Input
                type="number"
                inputMode="numeric"
                min={RULE_BOUNDS.durationMinutes.min}
                max={RULE_BOUNDS.durationMinutes.max}
                step={1}
                value={draft.durationMinutes}
                onChange={(event) => set({ durationMinutes: event.currentTarget.value })}
              />
            </Field>
          )}

          <Field
            label={t('spaces.pricingRules.minMinutes')}
            hint={`${t('spaces.pricingRules.minMinutesHint')} ${t('spaces.pricingRules.rangeHint', { ...RULE_BOUNDS.minMinutes })}`}
            error={errorFor('minMinutes')}
          >
            <Input
              type="number"
              inputMode="numeric"
              min={RULE_BOUNDS.minMinutes.min}
              max={RULE_BOUNDS.minMinutes.max}
              step={1}
              value={draft.minMinutes}
              placeholder={t('spaces.pricingRules.minMinutesNone')}
              onChange={(event) => set({ minMinutes: event.currentTarget.value })}
            />
          </Field>

          <div className="rounded-lg bg-[var(--muted)] p-3">
            <p className="mb-1 text-xs font-medium">{t('spaces.pricingRules.exampleHeading')}</p>
            <WorkedExample draft={draft} currency={currency} />
          </div>
        </section>

        {/* ---- when it applies -------------------------------------------- */}
        <section className="space-y-3 border-t pt-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <CalendarClock className="h-4 w-4 text-accent" aria-hidden />
            {t('spaces.pricingRules.whenHeading')}
          </h3>

          <SwitchRow
            label={t('spaces.pricingRules.whenLimited')}
            hint={
              windowed
                ? t('spaces.pricingRules.whenLimitedHint')
                : t('spaces.pricingRules.whenAlways')
            }
            checked={windowed}
            onChange={setWindowEnabled}
          />

          {windowed && (
            <div className="space-y-3">
              <fieldset className="space-y-1.5">
                <legend className="block text-sm font-medium">
                  {t('spaces.pricingRules.days')}
                </legend>
                <div className="flex flex-wrap gap-2">
                  {DAY_KEYS.map((key, day) => (
                    <label
                      key={key}
                      className="flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm"
                    >
                      <input
                        type="checkbox"
                        className="size-5"
                        checked={draft.daysOfWeek.includes(day)}
                        onChange={() => toggleDay(day)}
                      />
                      {t(`spaces.resources.day.${key}`)}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-ink-muted">{t('spaces.pricingRules.daysHint')}</p>
              </fieldset>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t('spaces.pricingRules.from')}>
                  <Input
                    type="time"
                    dir="ltr"
                    className="text-start"
                    value={draft.startTime}
                    onChange={(event) => set({ startTime: event.currentTarget.value })}
                  />
                </Field>
                <Field label={t('spaces.pricingRules.to')}>
                  <Input
                    type="time"
                    dir="ltr"
                    className="text-start"
                    value={draft.endTime}
                    onChange={(event) => set({ endTime: event.currentTarget.value })}
                  />
                </Field>
              </div>

              {/* The inclusive end is the single fact a venue writing adjacent
                  windows gets wrong, and it costs them a 409 they read as a bug. */}
              <p className="text-xs text-ink-muted">{t('spaces.pricingRules.windowHint')}</p>
              {errorFor('window') && (
                <p role="alert" className="text-xs text-red-600 dark:text-red-400">
                  {errorFor('window')}
                </p>
              )}
            </div>
          )}
        </section>

        {/* ---- which rule wins -------------------------------------------- */}
        <section className="space-y-3 border-t pt-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <Layers className="h-4 w-4 text-accent" aria-hidden />
            {t('spaces.pricingRules.priorityHeading')}
          </h3>
          <Field
            label={t('spaces.pricingRules.priority')}
            hint={
              activeRuleCount > (record ? 1 : 0)
                ? t('spaces.pricingRules.priorityHintCompeting', {
                    count: activeRuleCount - (record ? 1 : 0),
                  })
                : t('spaces.pricingRules.priorityHintAlone')
            }
            error={errorFor('priority')}
          >
            <Input
              type="number"
              inputMode="numeric"
              step={1}
              value={draft.priority}
              onChange={(event) => set({ priority: event.currentTarget.value })}
            />
          </Field>
        </section>

        {/* The draft's own name goes in as `subject`, because the ambiguity
            sentence is about two rules and the server only sends the one the
            manager cannot already see on their screen. */}
        {refusal && (
          <RefusalNotice refusal={refusal} names={{ subject: draft.name }} tone="danger" />
        )}
      </div>
    </Dialog>
  );
}

/** `spaces.pricingRules.errors.*` is a computed key, so `t` is applied here. */
function messageOf(
  problem: RuleProblem | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | undefined {
  return problem ? t(problem.key, problem.params) : undefined;
}

/**
 * "1 hour = SAR 40.00" — the sentence the whole editor is for.
 *
 * Priced with `priceForDuration` from `@/lib/domain`, the very function the
 * meter and the API bill with, rather than by multiplying the price out here.
 * The rounding is the part a manager cannot predict and most needs to see:
 * every unit rounds *up* to the next whole unit, so an hourly rule bills two
 * hours for a 61-minute visit. A screen that showed only "40.00 per hour" would
 * be hiding exactly the arithmetic that surprises people — and hiding it until
 * a guest is holding the bill.
 *
 * The amount is `formatCurrency`'d rather than printed from the draft string,
 * which matters more than it looks: `price` comes back from the API as a
 * `Decimal.js` `toJSON`, so `'25.5000'` is stored and `"25.5"` is sent, and
 * `'40.0000'` arrives as `"40"`. Anything that assumed four places — or two —
 * would render a rate the bill then disagrees with.
 */
export function WorkedExample({ draft, currency }: { draft: RuleDraft; currency: string }) {
  const { t, i18n } = useTranslation();
  const rows = workedExamples(draft);

  if (rows.length === 0) {
    return <p className="text-xs text-ink-muted">{t('spaces.pricingRules.exampleNeedsPrice')}</p>;
  }

  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
      {rows.map((example) => (
        <li key={example.minutes ?? 'flat'} className="flex items-center gap-1.5">
          <span className="text-ink-muted">
            {example.minutes === null
              ? t('spaces.pricingRules.exampleAnyLength')
              : t('spaces.pricingRules.exampleMinutes', { count: example.minutes })}
          </span>
          <span aria-hidden>=</span>
          {/* A price in a sentence of Arabic: the currency symbol and the digits
              are one LTR run and must not be split around the equals sign. */}
          <span className="font-medium tabular-nums" dir="ltr">
            {formatCurrency(example.amount, currency, i18n.language)}
          </span>
        </li>
      ))}
    </ul>
  );
}
